import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { useEditorTheme } from "../theme.js";
import ProblemStatement from "../components/ProblemStatement.js";
import McqAnswer from "../components/McqAnswer.js";
import {
  api,
  type Assignment,
  type Draft,
  type RunResult,
  type SubmissionDetail,
  type SubmissionSummary,
  type SubmitResponse,
} from "../api.js";

type Tab = "compiler" | "run" | "tests" | "history";

export default function Workspace() {
  const editorTheme = useEditorTheme();
  const { id } = useParams<{ id: string }>();

  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [code, setCode] = useState("");
  const [stdin, setStdin] = useState("");
  const [tab, setTab] = useState<Tab>("compiler");
  const [error, setError] = useState<string | null>(null);

  const [busy, setBusy] = useState<null | "compile" | "run" | "submit">(null);
  const [diagnostics, setDiagnostics] = useState<string | null>(null);
  const [compileOk, setCompileOk] = useState<boolean | null>(null);
  const [runResult, setRunResult] = useState<RunResult | null>(null);
  const [submitResult, setSubmitResult] = useState<SubmitResponse | null>(null);
  const [history, setHistory] = useState<SubmissionSummary[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  /** Which past submission's code is expanded in the History tab, if any. */
  const [viewing, setViewing] = useState<string | null>(null);

  // Tracks what has been persisted so autosave can skip no-op writes.
  const savedCode = useRef<string>("");

  useEffect(() => {
    if (!id) return;
    api
      .get<{ assignment: Assignment; draft: Draft | null }>(`/api/assignments/${id}`)
      .then((r) => {
        setAssignment(r.assignment);
        setDraft(r.draft);
        // A saved draft always wins over starter code -- losing work on reload
        // is the fastest way to make students distrust the tool.
        const initial = r.draft?.code ?? r.assignment.starterCode;
        setCode(initial);
        savedCode.current = initial;
        const sample = r.assignment.testCases.find((tc) => !tc.hidden && tc.stdin !== undefined);
        if (sample?.stdin) setStdin(sample.stdin);
      })
      .catch((e) => setError(e.message));

    api
      .get<{ submissions: SubmissionSummary[] }>(`/api/assignments/${id}/submissions/mine`)
      .then((r) => setHistory(r.submissions))
      .catch(() => {});
  }, [id]);

  // Autosave the draft a couple of seconds after typing stops.
  useEffect(() => {
    if (!id || code === savedCode.current) return;
    const timer = setTimeout(() => {
      api
        .put(`/api/assignments/${id}/draft`, { code })
        .then(() => (savedCode.current = code))
        .catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [code, id]);

  const compile = useCallback(async () => {
    if (!id) return;
    setBusy("compile");
    setError(null);
    setTab("compiler");
    try {
      const r = await api.post<{ ok: boolean; diagnostics: string }>(`/api/assignments/${id}/compile`, { code });
      setCompileOk(r.ok);
      setDiagnostics(r.diagnostics);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Compile failed.");
    } finally {
      setBusy(null);
    }
  }, [code, id]);

  const run = useCallback(async () => {
    if (!id) return;
    setBusy("run");
    setError(null);
    try {
      const r = await api.post<{ compiled: boolean; diagnostics: string; run: RunResult | null }>(
        `/api/assignments/${id}/run`,
        { code, stdin }
      );
      setCompileOk(r.compiled);
      setDiagnostics(r.diagnostics);
      setRunResult(r.run);
      // Send the student where the answer is: errors to the compiler tab,
      // output to the run tab.
      setTab(r.compiled ? "run" : "compiler");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Run failed.");
    } finally {
      setBusy(null);
    }
  }, [code, id, stdin]);

  const submit = useCallback(async () => {
    if (!id) return;
    if (!confirm("Submit this code for grading?")) return;
    setBusy("submit");
    setError(null);
    try {
      const r = await api.post<SubmitResponse>(`/api/assignments/${id}/submit`, { code });
      setSubmitResult(r);
      setCompileOk(r.compiled);
      setDiagnostics(r.compilerOutput);
      setTab(r.compiled ? "tests" : "compiler");
      const h = await api.get<{ submissions: SubmissionSummary[] }>(`/api/assignments/${id}/submissions/mine`);
      setHistory(h.submissions);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submit failed.");
    } finally {
      setBusy(null);
    }
  }, [code, id]);

  // Ctrl/Cmd+Enter runs, matching the muscle memory from every other editor.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        if (!busy) void run();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, run]);

  if (error && !assignment) return <div className="page"><div className="error-banner">{error}</div></div>;
  if (!assignment) return <div className="page muted">Loading…</div>;

  const overdue = assignment.dueAt ? new Date(assignment.dueAt).getTime() - Date.now() < 0 : false;
  const closed = overdue && !assignment.allowLate;

  // A sheet is a list of questions, not a question: the editor belongs to each
  // part, reached from here.
  if (assignment.parts.length > 0) {
    return <SheetView assignment={assignment} closed={closed} />;
  }

  // A multiple-choice question has nothing to compile, run or edit, so it gets
  // a page rather than the three-pane editor: statement, choices, submit.
  if (assignment.kind === "MCQ") {
    return (
      <McqWorkspace
        assignment={assignment}
        closed={closed}
        draft={draft}
        history={history}
        onSubmitted={(h) => setHistory(h)}
      />
    );
  }

  return (
    <div className="workspace">
      <div className="pane">
        <Link to="/assignments" className="muted" style={{ fontSize: 13 }}>← All assignments</Link>
        <h2 style={{ fontSize: 17, margin: "10px 0 6px" }}>{assignment.title}</h2>
        <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
          {assignment.maxMarks} marks
          {assignment.dueAt && (
            <> · due {new Date(assignment.dueAt).toLocaleString()}
              {overdue && <span className="badge fail" style={{ marginLeft: 6 }}>overdue</span>}
            </>
          )}
        </div>
        {overdue && !assignment.allowLate && (
          <div className="notice">The deadline has passed. Submissions are closed.</div>
        )}

        <ProblemStatement
          description={assignment.description}
          inputFormat={assignment.inputFormat}
          outputFormat={assignment.outputFormat}
          testCases={assignment.testCases}
        />

        <div className="muted" style={{ fontSize: 12, marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 10 }}>
          Limits: {assignment.timeLimitMs} ms · {Math.round(assignment.memoryLimitKb / 1024)} MB memory
        </div>
      </div>

      <div className="editor-column">
        <div className="toolbar">
          <button onClick={compile} disabled={busy !== null}>
            {busy === "compile" ? "Compiling…" : "Compile"}
          </button>
          <button onClick={run} disabled={busy !== null}>
            {busy === "run" ? "Running…" : "Run ▸"}
          </button>
          <div style={{ flex: 1 }} />
          {compileOk !== null && (
            <span className={`badge ${compileOk ? "pass" : "fail"}`}>
              {compileOk ? "compiles" : "compile error"}
            </span>
          )}
          <button
            className="primary"
            onClick={submit}
            disabled={busy !== null || (overdue && !assignment.allowLate)}
          >
            {busy === "submit" ? "Submitting…" : "Submit"}
          </button>
        </div>

        <Editor
          language="c"
          theme={editorTheme}
          value={code}
          onChange={(v) => setCode(v ?? "")}
          options={{
            fontSize: 13.5,
            minimap: { enabled: false },
            tabSize: 4,
            insertSpaces: true,
            scrollBeyondLastLine: false,
            automaticLayout: true,
            renderWhitespace: "selection",
          }}
        />

        <div className="output">
          <div className="tabs">
            <button className={tab === "compiler" ? "active" : ""} onClick={() => setTab("compiler")}>Compiler</button>
            <button className={tab === "run" ? "active" : ""} onClick={() => setTab("run")}>Run</button>
            <button className={tab === "tests" ? "active" : ""} onClick={() => setTab("tests")}>
              Tests{submitResult ? ` (${submitResult.tests.filter((t) => t.passed).length}/${submitResult.tests.length})` : ""}
            </button>
            <button className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>
              History{history.length ? ` (${history.length})` : ""}
            </button>
          </div>

          <div className="output-body">
            {error && <div className="error-banner">{error}</div>}

            {tab === "compiler" && (
              <pre className={`console ${diagnostics ? "" : "empty"}`}>
                {diagnostics || "Press Compile to check your code."}
              </pre>
            )}

            {tab === "run" && (
              <div className="grid-2">
                <label style={{ marginBottom: 0 }}>
                  <span>Input (stdin)</span>
                  <textarea rows={6} value={stdin} onChange={(e) => setStdin(e.target.value)} />
                </label>
                <div>
                  <span className="muted" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                    Output
                    {runResult && (
                      <> · {runResult.timeMs} ms
                        {runResult.timedOut && " · timed out"}
                        {runResult.signal && ` · ${runResult.signal}`}
                        {runResult.exitCode !== null && ` · exit ${runResult.exitCode}`}
                      </>
                    )}
                  </span>
                  <pre className={`console ${runResult?.stdout ? "" : "empty"}`}>
                    {runResult
                      ? runResult.stdout || "(no output)"
                      : "Press Run to execute with the input on the left."}
                  </pre>
                  {runResult?.stderr && (
                    <pre className="console" style={{ marginTop: 6, color: "var(--fail-text)" }}>{runResult.stderr}</pre>
                  )}
                  {runResult?.outputTruncated && (
                    <div className="notice" style={{ marginTop: 6 }}>Output was too long and got cut off.</div>
                  )}
                </div>
              </div>
            )}

            {tab === "tests" &&
              (submitResult ? (
                <>
                  <div className="between" style={{ marginBottom: 10 }}>
                    <strong>Score: {submitResult.score} / {submitResult.maxMarks}</strong>
                    {submitResult.submission.late && <span className="badge warn">late</span>}
                  </div>
                  {!submitResult.compiled && (
                    <div className="error-banner">Your code did not compile, so no tests were run.</div>
                  )}
                  {submitResult.tests.map((t, i) => (
                    <div className="test-row" key={i}>
                      <span className={`badge ${t.passed ? "pass" : "fail"}`}>{t.passed ? "pass" : "fail"}</span>
                      <span className="name">
                        {t.name} {t.hidden && <span className="muted" style={{ fontSize: 12 }}>(hidden)</span>}
                        {t.reason && !t.passed && <div className="muted" style={{ fontSize: 12.5 }}>{t.reason}</div>}
                        {!t.hidden && !t.passed && t.actualStdout !== undefined && (
                          <pre className="console" style={{ marginTop: 6 }}>
                            your output: {JSON.stringify(t.actualStdout)}
                          </pre>
                        )}
                      </span>
                      <span className="muted" style={{ fontSize: 12 }}>{t.timeMs} ms</span>
                    </div>
                  ))}
                </>
              ) : (
                <pre className="console empty">Submit to run your code against the test cases.</pre>
              ))}

            {tab === "history" &&
              (history.length === 0 ? (
                <pre className="console empty">No submissions yet.</pre>
              ) : (
                <table>
                  <thead>
                    <tr><th>Submitted</th><th>Status</th><th>Auto</th><th>Final</th><th>Comment</th><th /></tr>
                  </thead>
                  <tbody>
                    {history.map((s) => (
                      <Fragment key={s.id}>
                        <tr>
                          <td>
                            {new Date(s.submittedAt).toLocaleString()}{" "}
                            {s.late && <span className="badge warn">late</span>}
                          </td>
                          <td>{s.status.toLowerCase().replace("_", " ")}</td>
                          <td>{s.autoScore ?? "—"}</td>
                          <td>{s.manualScore ?? s.autoScore ?? "—"}</td>
                          <td className="muted">{s.teacherComment ?? ""}</td>
                          <td>
                            <button onClick={() => setViewing((v) => (v === s.id ? null : s.id))}>
                              {viewing === s.id ? "Hide code" : "View code"}
                            </button>
                          </td>
                        </tr>
                        {viewing === s.id && (
                          <tr>
                            <td colSpan={6}>
                              <PastSubmission id={s.id} onRestore={(c) => setCode(c)} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * One of the student's own past submissions, read-only.
 *
 * The History tab listed scores and timestamps and nothing else, so a student
 * could not look at what they actually wrote last week -- the thing they most
 * want when a mark surprises them, and the thing they need when building on
 * their own earlier work.
 *
 * Fetched per row rather than with the list: code is the large part of a
 * submission, and most rows are never opened. `GET /api/submissions/:id`
 * already served the owning student; only the way in was missing.
 */
function PastSubmission({ id, onRestore }: { id: string; onRestore: (code: string) => void }) {
  const [data, setData] = useState<SubmissionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ submission: SubmissionDetail }>(`/api/submissions/${id}`)
      .then((r) => setData(r.submission))
      .catch((e) => setError(e.message));
  }, [id]);

  if (error) return <div className="error-banner">{error}</div>;
  if (!data) return <div className="muted">Loading…</div>;

  const chosen = data.options.filter((o) => data.selectedOptionIds.includes(o.id));

  return (
    <div>
      {data.compilerOutput && (
        <>
          <div className="muted" style={{ fontSize: 12, margin: "4px 0" }}>COMPILER OUTPUT</div>
          <pre className="console">{data.compilerOutput}</pre>
        </>
      )}
      {data.options.length > 0 ? (
        <div className="muted" style={{ fontSize: 13 }}>
          You chose: {chosen.length ? chosen.map((o) => o.text).join(", ") : "nothing"}
        </div>
      ) : (
        <>
          <div className="between" style={{ margin: "4px 0" }}>
            <span className="muted" style={{ fontSize: 12 }}>YOUR CODE</span>
            {/* Copies it into the editor rather than submitting it: going back
                to an earlier attempt is a starting point, not an action. */}
            <button onClick={() => onRestore(data.code)}>Copy into editor</button>
          </div>
          <pre className="console">{data.code}</pre>
        </>
      )}
    </div>
  );
}

/**
 * A multiple-choice question, as a student answers it.
 *
 * Deliberately not the code workspace: there is nothing to compile or run, and
 * a Monaco pane beside four radio buttons would only ask "what am I meant to
 * type here?".
 */
function McqWorkspace({
  assignment,
  closed,
  draft,
  history,
  onSubmitted,
}: {
  assignment: Assignment;
  closed: boolean;
  draft: { selectedOptions: string } | null;
  history: SubmissionSummary[];
  onSubmitted: (history: SubmissionSummary[]) => void;
}) {
  // Ticked options survive a reload, the same way code does.
  const [selected, setSelected] = useState<string[]>(
    draft?.selectedOptions ? draft.selectedOptions.split(",") : []
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ score: number; correct?: boolean } | null>(null);

  // How many answers to expect is the teacher's design, not something derived
  // from the key -- which a student never receives.
  const multiple = assignment.expectedAnswers > 1;

  async function submit() {
    if (selected.length === 0) {
      setError("Choose an answer first.");
      return;
    }
    if (!confirm("Submit this answer?")) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ score: number; correct: boolean }>(
        `/api/assignments/${assignment.id}/submit`,
        { selectedOptionIds: selected }
      );
      setResult({ score: r.score, correct: r.correct });
      const h = await api.get<{ submissions: SubmissionSummary[] }>(
        `/api/assignments/${assignment.id}/submissions/mine`
      );
      onSubmitted(h.submissions);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submit failed.");
    } finally {
      setBusy(false);
    }
  }

  const latest = history[0];

  return (
    <div className="page">
      <Link to="/assignments" className="muted" style={{ fontSize: 13 }}>← All assignments</Link>
      <h1 style={{ fontSize: 22, margin: "10px 0 6px" }}>{assignment.title}</h1>
      <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
        {assignment.maxMarks} marks · multiple choice
        {assignment.dueAt && <> · due {new Date(assignment.dueAt).toLocaleString()}</>}
      </div>

      {closed && <div className="notice">The deadline has passed. Submissions are closed.</div>}
      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        <ProblemStatement description={assignment.description} />

        {multiple && (
          <p className="muted" style={{ fontSize: 13 }}>
            Choose {assignment.expectedAnswers} options. All of them must be right.
          </p>
        )}

        <div style={{ marginTop: 16 }}>
          <McqAnswer
            options={assignment.options ?? []}
            selected={selected}
            onChange={(ids) => {
              setSelected(ids);
              api
                .put(`/api/assignments/${assignment.id}/draft`, { selectedOptionIds: ids })
                .catch(() => {});
            }}
            multiple={multiple}
            disabled={closed || busy}
          />
        </div>

        <div className="row" style={{ marginTop: 16 }}>
          <button className="primary" onClick={submit} disabled={closed || busy}>
            {busy ? "Submitting…" : "Submit answer"}
          </button>
          {latest && (
            <span className="muted" style={{ fontSize: 13 }}>
              Last submitted {new Date(latest.submittedAt).toLocaleString()} ·{" "}
              {latest.manualScore ?? latest.autoScore ?? "—"} / {assignment.maxMarks}
            </span>
          )}
        </div>

        {result && (
          <div
            className={result.correct ? "notice" : "error-banner"}
            style={{ marginTop: 14, marginBottom: 0 }}
          >
            {result.correct
              ? `Correct — ${result.score} / ${assignment.maxMarks}.`
              : `Not correct — ${result.score} / ${assignment.maxMarks}. You can submit again.`}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A multi-part assignment, as a student sees it: the questions, what each is
 * worth, and which are done.
 *
 * Each question is opened on its own — the work, the compiler and the marks all
 * belong to the question, so this page only has to get them there and show
 * where they are up to.
 */
function SheetView({ assignment, closed }: { assignment: Assignment; closed: boolean }) {
  const done = assignment.parts.filter((p) => p.myLatest).length;
  const earned = assignment.parts.reduce((sum, p) => sum + (p.myLatest?.score ?? 0), 0);
  const total = assignment.parts.reduce((sum, p) => sum + p.maxMarks, 0);

  return (
    <div className="page">
      <Link to="/assignments" className="muted" style={{ fontSize: 13 }}>← All assignments</Link>
      <h1 style={{ fontSize: 22, margin: "10px 0 6px" }}>{assignment.title}</h1>
      <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
        {assignment.parts.length} question{assignment.parts.length === 1 ? "" : "s"} · {total} marks
        {assignment.dueAt && <> · due {new Date(assignment.dueAt).toLocaleString()}</>}
        {done > 0 && ` · ${done} of ${assignment.parts.length} answered`}
      </div>

      {closed && <div className="notice">The deadline has passed. Submissions are closed.</div>}

      {assignment.description.trim() && (
        <div className="card">
          <ProblemStatement description={assignment.description} />
        </div>
      )}

      <div className="card">
        {assignment.parts.map((p, i) => (
          <div className="test-row" key={p.id}>
            <span className="muted">{i + 1}.</span>
            <span className="name">
              {p.title}{" "}
              {p.kind === "MCQ" && <span className="badge draft">multiple choice</span>}
            </span>
            <span className="muted">{p.maxMarks} marks</span>
            {p.myLatest ? (
              <span
                className={`badge ${
                  (p.myLatest.score ?? 0) >= p.maxMarks ? "pass" : "warn"
                }`}
              >
                {p.myLatest.score ?? "—"} / {p.maxMarks}
              </span>
            ) : (
              <span className="badge draft">not answered</span>
            )}
            <Link to={`/assignments/${p.id}`}>
              <button className={p.myLatest ? "" : "primary"}>
                {p.myLatest ? "Open" : "Start"}
              </button>
            </Link>
          </div>
        ))}
      </div>

      {done > 0 && (
        <div className="card">
          <strong>
            {earned} / {total} marks so far
          </strong>
          <div className="muted" style={{ fontSize: 13 }}>
            Each question is marked on its own; this is their total. You can resubmit any question
            until the deadline.
          </div>
        </div>
      )}
    </div>
  );
}
